import { useEffect, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Link } from "@corpuscomplete/web/components/link";
import { Avatar, AvatarFallback, AvatarImage } from "@corpuscomplete/web-ui/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { useLogout, useUser } from "../auth/hooks";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@corpuscomplete/web-ui/components/sidebar";
import {
  getScopedRouteScopeFromPathname,
  writeLastScopedRouteScope,
} from "@corpuscomplete/web/modules/common/last-scoped-route-scope";

/**
 * Primary app navigation for authenticated users.
 * Contract: render route-entry links and user actions from auth hooks;
 * permission logic stays in domain/auth layers, not in sidebar state.
 */
export function AppSidebar() {
  const user = useUser();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    const scopeFromPathname = getScopedRouteScopeFromPathname(pathname, user.id);
    if (scopeFromPathname) {
      writeLastScopedRouteScope(user.id, scopeFromPathname);
    }
  }, [pathname, user.id]);

  return (
    <Sidebar variant="inset">
      <SidebarRail />
      <SidebarHeader>
        <UserProfile />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <Link to="/">
                <SidebarMenuButton>
                  <Text variant="sm">Home</Text>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Nutrition</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <Link to="/personal/food-log">
                  <SidebarMenuButton>
                    <Text variant="sm">Food Log</Text>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/personal/programs">
                  <SidebarMenuButton>
                    <Text variant="sm">Programs</Text>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <ScopedSidebarLink section="recipes">
                  <SidebarMenuButton isActive={isScopedSectionActive(pathname, "recipes")}>
                    <Text variant="sm" className="leading-">
                      Recipes
                    </Text>
                  </SidebarMenuButton>
                </ScopedSidebarLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <ScopedSidebarLink section="collections">
                  <SidebarMenuButton isActive={isScopedSectionActive(pathname, "collections")}>
                    <Text variant="sm">Collections</Text>
                  </SidebarMenuButton>
                </ScopedSidebarLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/personal/meal-plans">
                  <SidebarMenuButton>
                    <Text variant="sm">Meal Plans</Text>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <ScopedSidebarLink section="planned-meals">
                  <SidebarMenuButton isActive={isScopedSectionActive(pathname, "planned-meals")}>
                    <Text variant="sm">Planned Meals</Text>
                  </SidebarMenuButton>
                </ScopedSidebarLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <ScopedSidebarLink section="meal-planning-sessions">
                  <SidebarMenuButton
                    isActive={isScopedSectionActive(pathname, "meal-planning-sessions")}
                  >
                    <Text variant="sm">Meal Planning Sessions</Text>
                  </SidebarMenuButton>
                </ScopedSidebarLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>More</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <Link to="/groups">
                  <SidebarMenuButton>
                    <Text variant="sm">Groups</Text>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <Link to="/settings">
                  <SidebarMenuButton>
                    <Text variant="sm">Settings</Text>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              {user.role === "admin" && (
                <SidebarMenuItem>
                  <Link to="/admin/ai-issues">
                    <SidebarMenuButton>
                      <Text variant="sm">Admin</Text>
                    </SidebarMenuButton>
                  </Link>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

function ScopedSidebarLink({
  children,
  section,
}: {
  children: ReactNode;
  section: "collections" | "meal-planning-sessions" | "planned-meals" | "recipes";
}) {
  switch (section) {
    case "collections":
      return <Link to="/collections">{children}</Link>;
    case "meal-planning-sessions":
      return <Link to="/meal-planning-sessions">{children}</Link>;
    case "planned-meals":
      return <Link to="/planned-meals">{children}</Link>;
    case "recipes":
      return <Link to="/recipes">{children}</Link>;
  }
}

function isScopedSectionActive(
  pathname: string,
  section: "collections" | "meal-planning-sessions" | "planned-meals" | "recipes",
): boolean {
  return (
    pathname === `/${section}` ||
    pathname.startsWith(`/${section}/`) ||
    new RegExp(`^/personal/${section}(?:/|$)`).test(pathname) ||
    new RegExp(`^/spaces/[^/]+/${section}(?:/|$)`).test(pathname)
  );
}

function UserProfile() {
  const user = useUser();
  const logout = useLogout();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="group flex w-full items-center gap-2 rounded-md p-1 outline-none hover:bg-gray-200">
        <div className="flex cursor-default flex-row items-center gap-2">
          <Avatar className="h-6 w-6">
            <AvatarImage src={user.image || undefined} alt={user.name} />
            <AvatarFallback>
              <Text variant="sm">{user.name.charAt(0)}</Text>
            </AvatarFallback>
          </Avatar>
          <Text variant="sm" className="truncate font-medium group-hover:text-gray-900">
            {user.name}
          </Text>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[184px]">
        <DropdownMenuGroup>
          <DropdownMenuItem>
            <Link to="/settings" className="w-full cursor-pointer">
              <Text variant="sm">Settings</Text>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
            <Text variant="sm">Log Out</Text>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
